import React, { useState, useEffect } from 'react';
import {
  Box, Typography, CircularProgress, Alert, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Avatar,
  alpha
} from '@mui/material';
import {
  RefreshCw, Check, Phone, Clock
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth0 } from '@auth0/auth0-react';

// Import the CircleDataContext hook
import { useCircleData } from './CircleDataContext';

// Import our component views
import CircleTabListView from './CircleTabListView';
import CircleTabDetailView from './CircleTabDetailView';
import './CircleTabPanelNavigation.css'; // Make sure to import the CSS

// Import service modules
import { circleService } from '../../services/circleService';

/**
 * Main container component for the Circle Tab Panel Navigation
 * Handles:
 * - High-level state management
 * - Routing between list and detail views
 * - Shared utility functions
 * - Port initiation process with enhanced order identification
 * 
 * Now uses CircleDataContext and service modules instead of direct API calls
 */
const CircleTabPanelNavigation = ({ darkMode = false }) => {
  // View state
  const [activeView, setActiveView] = useState('circles');
  const [selectedCircle, setSelectedCircle] = useState(null);

  // UI and local state
  const [refreshAnimation, setRefreshAnimation] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [circleDetail, setCircleDetail] = useState(null);
  const [pricingData, setPricingData] = useState(null);
  const [circleMembers, setCircleMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState(null);

  // Port dialog state
  const [portDialogOpen, setPortDialogOpen] = useState(false);
  const [portingCircle, setPortingCircle] = useState(null);
  const [portAction, setPortAction] = useState(null);
  const [portSubscriptionId, setPortSubscriptionId] = useState(null); // Store subscription ID
  const [verificationCode, setVerificationCode] = useState('');
  const [portingStatus, setPortingStatus] = useState(null); // 'loading', 'success', 'error'
  const [portingError, setPortingError] = useState(null);

  // Auth0 hooks for authentication
  const { getAccessTokenSilently } = useAuth0();

  // Local state to mimic the original implementation
  const [circles, setCircles] = useState([]);
  const [ownedCircles, setOwnedCircles] = useState([]);
  const [joinedCircles, setJoinedCircles] = useState([]);
  const [dataInitialized, setDataInitialized] = useState(false);

  // Get shared data from context
  const {
    userCircles,        // All circles from the API
    isLoading,          // Loading state
    error,              // Error state
    refreshData,        // Function to refresh data
    circleData,         // Selected circle details
    tierData,           // Processed tier data
    tiers,              // Array of pricing tiers
    selectedCircleId,   // Currently selected circle ID
    setSelectedCircleId // Function to change selected circle
  } = useCircleData();

  // Helper function to determine shipping method correctly
  const determineShippingMethod = (circle) => {
    // Log what we're working with
    console.log(`TabPanelNav - Circle ${circle.name} shipping method:`, {
      hasMethod: 'shipping_method' in circle,
      hasMethodAlt: 'shippingMethod' in circle,
      rawMethod: circle.shipping_method,
      altMethod: circle.shippingMethod,
      status: circle.subscription_status || circle.statusDisplay
    });

    // Check direct properties first
    if (circle.shipping_method) return circle.shipping_method;
    if (circle.shippingMethod) return circle.shippingMethod;

    // Look at subscription status 
    const status = (circle.subscription_status || circle.statusDisplay || '').toLowerCase();

    // If status contains 'activate' and no shipping method is available, likely eSIM
    if (status.includes('activate') || status.includes('esim_initiated')) {
      console.log(`TabPanelNav - Setting eSIM for activate/esim_initiated status circle: ${circle.name}`);
      return 'eSIM';
    }

    if (status.includes('esim') || status.includes('esim_initiated')) {
      return 'eSIM';
    }

    // Last resort - default to standard but log it
    console.log(`TabPanelNav - Defaulting to standard shipping for ${circle.name}`);
    return 'standard';
  };

  // Enhanced useEffect with debugging for userCircles
  useEffect(() => {
    if (userCircles && userCircles.length > 0) {
      // Add detailed logging of the first few circles
      console.log("DETAILED CIRCLE DATA:");
      userCircles.slice(0, 3).forEach((circle, index) => {
        console.log(`Circle ${index + 1} (${circle.name}):`, {
          id: circle.id,
          statusDisplay: circle.statusDisplay,
          subscription_status: circle.subscription_status,
          shipping_method: circle.shipping_method,
          joining_condition: circle.joining_condition,
          isOwner: circle.isOwner,
          // Also log stripe_subscription_id if available
          stripe_subscription_id: circle.stripe_subscription_id,
          // Check what properties exist on the object
          hasStatusDisplay: 'statusDisplay' in circle,
          hasSubscriptionStatus: 'subscription_status' in circle,
          hasShippingMethod: 'shipping_method' in circle,
          hasJoiningCondition: 'joining_condition' in circle,
          hasStripeSubId: 'stripe_subscription_id' in circle,
          // List all keys for inspection
          allPropertyNames: Object.keys(circle)
        });
      });

      // Check if any circles have shipment status and switching carriers
      const debugCircles = userCircles.filter(circle => {
        const status = circle.subscription_status || circle.statusDisplay || '';
        const joiningCondition = circle.joining_condition || '';

        return (
          (status.toLowerCase().includes('ship') || status.toLowerCase().includes('active')) &&
          joiningCondition.toLowerCase().includes('switch')
        );
      });

      console.log(`Found ${debugCircles.length} circles matching port conditions:`,
        debugCircles.map(c => ({
          id: c.id,
          name: c.name,
          status: c.subscription_status || c.statusDisplay,
          joining: c.joining_condition,
          subId: c.stripe_subscription_id
        }))
      );

      // Enrich circles with required properties if missing (TEMPORARY SOLUTION)
      const enrichedCircles = userCircles.map(circle => {
        // Check if the circle has a subscription status with "shipment"
        const hasShipmentStatus =
          (circle.subscription_status && circle.subscription_status.toLowerCase().includes('ship')) ||
          (circle.statusDisplay && circle.statusDisplay.toLowerCase().includes('ship'));

        // Return improved enriched circle with better shipping method detection
        return {
          ...circle,
          // Ensure subscription_status exists
          subscription_status: circle.subscription_status || circle.statusDisplay || (hasShipmentStatus ? 'shipment' : 'unknown'),

          // FIXED: Better shipping method detection with intelligent default
          shipping_method: determineShippingMethod(circle),

          // Ensure joining_condition exists (make eligible for port)
          joining_condition: circle.joining_condition || circle.joiningCondition || 'switching_carriers',

          // Add debug flag to identify enriched circles
          _enriched: true
        };
      });

      // Update all circles with enriched data
      setCircles(enrichedCircles);

      // Filter based on isOwner property - EXACT SAME LOGIC AS ORIGINAL
      const owned = enrichedCircles.filter(circle => circle.isOwner);
      const joined = enrichedCircles.filter(circle => !circle.isOwner);

      // Debug logging
      console.log('Circles breakdown:', {
        total: enrichedCircles.length,
        owned: owned.length,
        joined: joined.length,
        ownedIds: owned.map(c => c.id),
        joinedIds: joined.map(c => c.id)
      });

      // Update state
      setOwnedCircles(owned);
      setJoinedCircles(joined);
      setDataInitialized(true);
    } else {
      console.log("No circles data available or empty array received");
    }
  }, [userCircles]);

  // When circleData changes due to selectedCircleId, update detail view
  useEffect(() => {
    if (circleData && selectedCircleId && activeView === 'details') {
      // We have circle data from the context
      setCircleDetail(circleData);

      // Format pricing data from tierData and tiers for the detail view component
      if (tierData) {
        // Get the base single line price for savings calculations
        const getBaseSingleLinePrice = () => {
          if (!tiers || tiers.length === 0) return 0;

          // Find the tier with the highest price (usually single line tier)
          const singleLineTier = tiers.find(tier => tier.members === 1) ||
            tiers.reduce((prev, current) =>
              (prev.price > current.price) ? prev : current);

          return singleLineTier.price || 0;
        };

        // Map tierData to the format expected by CircleTabDetailView
        const formattedPricing = {
          pricingTiers: tiers.map(tier => ({
            line_count: tier.members,
            price_per_line: tier.price,
            savings_percentage: tier.savings,
            savings_amount: getBaseSingleLinePrice() - tier.price
          })),
          currentTier: {
            line_count: tierData.currentTier.line_count,
            price_per_line: tierData.currentTier.price_per_line,
            savings_percentage: tiers.find(t => t.isCurrent)?.savings || 0,
            savings_amount: getBaseSingleLinePrice() - tierData.currentTier.price_per_line
          },
          nextTier: tierData.nextTier ? {
            line_count: tierData.nextTier.line_count,
            price_per_line: tierData.nextTier.price_per_line
          } : null,
          savingsPerLine: tierData.nextTier ?
            (tierData.currentTier.price_per_line - tierData.nextTier.price_per_line) : 0,
          totalMembersAfterNextTier: tierData.nextTier ?
            tierData.nextTier.line_count : tierData.currentTier.line_count,
          potentialMonthlySavings: tierData.nextTier ?
            ((tierData.currentTier.price_per_line - tierData.nextTier.price_per_line) * tierData.nextTier.line_count) : 0,
          annualSavings: tierData.nextTier ?
            ((tierData.currentTier.price_per_line - tierData.nextTier.price_per_line) * tierData.nextTier.line_count * 12) : 0,
          membersToNextTier: tierData.membersToNextTier,
          totalMembers: tierData.totalJoiners,
          availableSlots: tierData.availableSlots,
          features: tierData.features,
          maxCapacity: tierData.maxTiers
        };

        setPricingData(formattedPricing);
      }

      setDetailLoading(false);
      setDetailError(null);
    }
  }, [circleData, selectedCircleId, tierData, tiers, activeView]);

  // Function to fetch circle members - now using the circleService
  const fetchCircleMembers = async (circleId) => {
    try {
      setMembersLoading(true);
      setMembersError(null);

      // Get the auth token for the API call
      const token = await getAccessTokenSilently({
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        scope: 'openid profile email'
      });

      // Use our new service method instead of mock data
      const response = await circleService.getCircleMembers(circleId, token);
      const members = response.data;

      setCircleMembers(members);
      return members;
    } catch (error) {
      console.error("Error fetching circle members:", error);
      setMembersError("Could not load circle members. Please try again later.");
      return [];
    } finally {
      setMembersLoading(false);
    }
  };

  // Handle selection of a circle
  const handleCircleSelect = async (circle) => {
    try {
      // Validate circle
      if (!circle || !circle.id) {
        console.error('Invalid circle selected:', circle);
        setDetailError('Invalid circle data. Please try selecting another circle.');
        return;
      }

      // Set loading state
      setDetailLoading(true);
      setDetailError(null);

      // Set the selected circle ID in context - this will trigger data fetching
      setSelectedCircleId(circle.id);

      // Set the selected circle in local state
      setSelectedCircle(circle);

      // Fetch members for the selected circle using our new service function
      const members = await fetchCircleMembers(circle.id);

      // Update the circle with fetched members
      setSelectedCircle(prev => ({
        ...prev,
        members
      }));

      // Set active view to details
      setActiveView('details');

    } catch (err) {
      console.error('Error in handleCircleSelect:', err);
      setDetailError('Error loading circle details. Please try again later.');
      setDetailLoading(false);
    }
  };

  // Handle returning to circle list
  const handleBackToCircles = () => {
    setActiveView('circles');
    setSelectedCircle(null);
    setCircleDetail(null);
    setPricingData(null);
    setCircleMembers([]);
    setDetailError(null);
  };

  // Handle refresh button click
  const handleRefresh = () => {
    setRefreshAnimation(true);
    refreshData(); // Use the refreshData function from context
    setTimeout(() => setRefreshAnimation(false), 1000);
  };

  // Handle sharing a circle (pass to detail view)
  const handleShareCircle = async (circleId) => {
    try {
      // Get the auth token
      const token = await getAccessTokenSilently({
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        scope: 'openid profile email'
      });

      // In a real implementation, call circleService to share the circle
      // Example: await circleService.shareCircle(circleId, {}, token);

      console.log("Sharing circle:", selectedCircle?.name);

      // This would typically call an API endpoint via service module
      return true; // Return success status
    } catch (error) {
      console.error("Error sharing circle:", error);
      return false;
    }
  };

  // In CircleTabPanelNavigation.js
  const handlePortAction = async (circle, action, preliminarySubId = null) => {
    console.log(`Port action triggered: ${action} for circle:`, circle.name);

    // Log if we already have a preliminary subscription ID from the view
    if (preliminarySubId && preliminarySubId.startsWith('sub_')) {
      console.log(`Using preliminary subscription ID from view: ${preliminarySubId}`);
      // Store it immediately
      setPortSubscriptionId(preliminarySubId);
    } else {
      console.log(`No valid preliminary subscription ID, will fetch via API`);
    }

    try {
      // Always start by setting basic state
      setPortingCircle(circle);
      setPortAction(action);

      // If we don't have a preliminary ID, get the auth token and fetch via API
      if (!preliminarySubId || !preliminarySubId.startsWith('sub_')) {
        const token = await getAccessTokenSilently({
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
          scope: 'openid profile email'
        });

        // STEP 1: Fetch the order details to get the subscription ID
        console.log(`API CALL: Fetching order details for circle ${circle.id}`);
        let enhancedCircle = { ...circle }; // Create a copy to enhance

        try {
          const orderResponse = await circleService.getUserOrderForCircle(circle.id, token);

          if (orderResponse.data && orderResponse.data.stripe_subscription_id) {
            console.log(`API SUCCESS: Got subscription ID: ${orderResponse.data.stripe_subscription_id}`);

            // Now we have the real subscription ID from the API
            setPortSubscriptionId(orderResponse.data.stripe_subscription_id);

            // Also enhance the circle object with additional data
            enhancedCircle = {
              ...circle,
              stripe_subscription_id: orderResponse.data.stripe_subscription_id,
              order_number: orderResponse.data.order_number
            };

            // Update portingCircle with the enhanced data
            setPortingCircle(enhancedCircle);
          } else {
            console.warn(`API WARNING: No subscription ID found for circle ${circle.id}`);
            // Still use the original circle but clear the subscription ID
            setPortSubscriptionId(null);
          }
        } catch (orderError) {
          console.error(`API ERROR: Could not fetch order details for circle ${circle.id}:`, orderError);
          // Continue with original circle data, but ensure portSubscriptionId is null
          setPortSubscriptionId(null);
        }
      } else {
        // We already have a valid subscription ID from the view
        console.log(`OPTIMIZATION: Using preliminary subscription ID: ${preliminarySubId}`);

        // Enhance the circle with the subscription ID we already have
        const enhancedCircle = {
          ...circle,
          stripe_subscription_id: preliminarySubId
        };

        // Update the state with the enhanced circle
        setPortingCircle(enhancedCircle);
      }

      // Get the final subscription ID to use (either from API or preliminary)
      const finalSubId = preliminarySubId?.startsWith('sub_') ?
        preliminarySubId :
        portSubscriptionId;

      // Log complete debug info
      console.log("PORT ACTION DEBUG INFO:", {
        circleId: circle.id,
        circleName: circle.name,
        status: circle.subscription_status || circle.statusDisplay,
        joiningCondition: circle.joining_condition,
        shippingMethod: circle.shipping_method,
        action: action,
        preliminarySubId: preliminarySubId,
        finalSubscriptionId: finalSubId
      });

      // Reset form state
      setVerificationCode('');
      setPortingStatus(null);
      setPortingError(null);

      // Open the dialog
      setPortDialogOpen(true);
    } catch (error) {
      console.error('Error preparing port action:', error);
    }
  };

  // Updated handlePortSubmit to use stripe_subscription_id as primary identifier
  const handlePortSubmit = async () => {
    // Validate input
    if (!portingCircle) {
      setPortingError('Invalid circle data. Please try again.');
      return;
    }
  
    if (!portAction) {
      setPortingError('Invalid action. Please try again.');
      return;
    }
  
    if (!verificationCode || verificationCode.length !== 4 || !/^\d{4}$/.test(verificationCode)) {
      setPortingError('Please enter the last 4 digits of your temporary number');
      return;
    }
  
    // Define primaryIdentifier at function scope so it's available in catch block
    let primaryIdentifier = null;
  
    try {
      setPortingStatus('loading');
  
      // Get the auth token for API call
      const token = await getAccessTokenSilently({
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        scope: 'openid profile email'
      });
  
      // Log complete port request information
      console.log(`PORT SUBMIT - Complete data for ${portingCircle.name}:`, {
        circle: portingCircle,
        action: portAction,
        subscriptionId: portSubscriptionId,
        verificationCode
      });
  
      // Determine the best identifier to use - SUBSCRIPTION ID FIRST
      
      // PRIORITY 1: Always use subscription ID if available
      if (portSubscriptionId && portSubscriptionId.startsWith('sub_')) {
        primaryIdentifier = portSubscriptionId;
        console.log(`Using subscription ID as primary identifier: ${primaryIdentifier}`);
      }
      // PRIORITY 2: Fall back to order number if available
      else if (portingCircle.order_number) {
        primaryIdentifier = portingCircle.order_number;
        console.log(`Using order number as fallback identifier: ${primaryIdentifier}`);
      }
      // PRIORITY 3: Last resort - use circle ID
      else {
        primaryIdentifier = portingCircle.id;
        console.log(`Using circle ID as last resort identifier: ${primaryIdentifier}`);
      }
  
      // Create comprehensive data package
      const portData = {
        // Required field for the endpoint
        last_four_digits: verificationCode,
        
        // CRITICAL: Always include the subscription ID in the request data
        // This ensures the backend finds it in request.data.get('stripe_subscription_id')
        ...(portSubscriptionId ? { stripe_subscription_id: portSubscriptionId } : {}),
        
        // Include all possible identifiers to help the backend
        circle_id: portingCircle.id,
        circle_name: portingCircle.name,
        order_number: portingCircle.order_number,
        
        // Add relevant context about the port
        joining_condition: portingCircle.joining_condition || 'switching_carriers',
        shipping_method: portingCircle.shipping_method || 'standard',
        port_action: portAction
      };
  
      console.log(`Processing ${portAction} using identifier: ${primaryIdentifier}`);
      console.log(`Request data:`, portData);
  
      // Use the circleService to submit the request
      if (portAction === 'confirm_sim' || portAction === 'initiate_port') {
        // IMPORTANT: Pass the subscription ID as the identifier AND include it in the data
        await circleService.confirmSIMReceipt(primaryIdentifier, token, portData);
        console.log(`${portAction} request completed successfully`);
      }
  
      // Set success state
      setPortingStatus('success');
  
      // Handle the refresh and dialog closing after a short delay
      setTimeout(async () => {
        try {
          // Access the existing refreshData function from props or context
          // No need to directly modify userCircles state as refreshData will handle that
          console.log('Refreshing data after successful port action');
          
          // Use refreshData which should be available from props or context
          if (typeof refreshData === 'function') {
            // Call with force=true to ensure fresh data
            await refreshData(true);
            console.log('Data refreshed successfully');
          } else {
            console.warn('refreshData function not available - UI may not update immediately');
          }
          
          // Close dialog and reset state
          setPortDialogOpen(false);
          setPortingCircle(null);
          setPortAction(null);
          setPortSubscriptionId(null);
          setVerificationCode('');
          setPortingStatus(null);
          
          console.log('Port action completed successfully');
        } catch (refreshError) {
          console.error('Error refreshing data after successful port action:', refreshError);
          
          // Still close the dialog even if refresh fails
          setPortDialogOpen(false);
          setPortingCircle(null);
          setPortAction(null);
          setPortSubscriptionId(null);
          setVerificationCode('');
          setPortingStatus(null);
        }
      }, 2000);
  
    } catch (error) {
      console.error('Error with porting action:', error);
      setPortingStatus('error');
  
      // Extract meaningful error message for user
      let errorMessage = 'An error occurred while processing your request';
  
      if (error.response && error.response.data) {
        if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
  
      setPortingError(errorMessage);
  
      // Log detailed error information
      console.log('Detailed error information:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        circle: portingCircle.name,
        primaryIdentifier,
        subscriptionId: portSubscriptionId
      });
    }
  };

  // Get color for carrier
  const getCarrierColor = (carrier) => {
    // Handle carrier being an object with name/slug properties
    let carrierSlug = '';

    if (typeof carrier === 'object' && carrier !== null) {
      // If carrier is an object, extract the slug or name
      carrierSlug = carrier.slug || carrier.name || '';
    } else if (typeof carrier === 'string') {
      // If carrier is already a string, use it directly
      carrierSlug = carrier;
    }

    // Now we have a string, we can safely use toLowerCase()
    switch (carrierSlug.toLowerCase()) {
      case 'verizon': return '#FF0000';
      case 'att': return '#0057B8';
      case 'tmobile': return '#E20074';
      case 'cricket': return '#6CBD45';
      default: return '#4E6FFF'; // Default blue color
    }
  };

  // Get color for status
  const getStatusColor = (status) => {
    // Convert status to a standardized format for comparison (first word capitalized)
    const normalizedStatus = typeof status === 'string'
      ? status.trim().toLowerCase()
      : '';

    // Case-insensitive matching
    if (normalizedStatus.includes('active') || normalizedStatus.includes('approved')) {
      return '#4CAF50'; // Green
    }
    if (normalizedStatus.includes('pending') || normalizedStatus.includes('approval')) {
      return '#FFC107'; // Yellow/Amber
    }
    if (normalizedStatus.includes('declined') || normalizedStatus.includes('decline')) {
      return '#F44336'; // Red
    }
    if (normalizedStatus.includes('shipment') || normalizedStatus.includes('esim') ||
      normalizedStatus.includes('sim_received')) {
      return '#2196F3'; // Blue
    }
    if (normalizedStatus.includes('transfer')) {
      return '#9C27B0'; // Purple
    }
    if (normalizedStatus.includes('cancel')) {
      return '#FF9800'; // Orange
    }

    // Default gray color for unknown statuses
    return '#757575';
  };

  // JSX for the main component
  return (
    <Box
      className={`circle-tab-container ${darkMode ? 'dark-mode' : ''}`}
      sx={{
        width: '100%',
        maxWidth: '100%',
        margin: '0 auto',
        position: 'relative',
        overflow: 'hidden',
        background: 'transparent !important',
        backgroundColor: 'transparent !important'
      }}
    >
      {/* Background Blobs - using CSS classes */}
      <div className="background-blobs">
        <div className="blob blob-1"></div>
        <div className="blob blob-2"></div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 8
          }}>
            <CircularProgress size={48} sx={{ color: '#FF4775', mb: 2 }} />
            <Typography
              variant="body2"
              sx={{ color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)' }}
            >
              Loading your circles...
            </Typography>
          </Box>
        </motion.div>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Alert
            severity="error"
            sx={{
              mb: 2,
              borderRadius: 2,
              '& .MuiAlert-icon': { alignSelf: 'center' }
            }}
            action={
              <Button
                className="refresh-button"
                color="error"
                size="small"
                onClick={handleRefresh}
                startIcon={<RefreshCw size={16} />}
              >
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        </motion.div>
      )}

      {/* Main Content */}
      {!isLoading && !error && (
        <>
          {activeView === 'circles' ? (
            <CircleTabListView
              circles={circles}
              ownedCircles={ownedCircles}
              joinedCircles={joinedCircles}
              dataInitialized={dataInitialized}
              onCircleSelect={handleCircleSelect}
              onRefresh={handleRefresh}
              refreshAnimation={refreshAnimation}
              getCarrierColor={getCarrierColor}
              getStatusColor={getStatusColor}
              darkMode={darkMode}
              onPortAction={handlePortAction} // Pass the enhanced port action handler
            />
          ) : (
            <CircleTabDetailView
              circle={selectedCircle}
              circleDetail={circleDetail}
              pricingData={pricingData}
              isLoading={detailLoading || (selectedCircleId && !circleData)}
              error={detailError}
              members={circleMembers}
              membersLoading={membersLoading}
              membersError={membersError}
              onBackToCircles={handleBackToCircles}
              onShareCircle={handleShareCircle}
              getCarrierColor={getCarrierColor}
              getStatusColor={getStatusColor}
              darkMode={darkMode}
              onPortAction={handlePortAction} // Pass the enhanced port action handler
            />
          )}
        </>
      )}

      {/* Port Verification Dialog */}
      <Dialog
        open={portDialogOpen}
        onClose={() => portingStatus !== 'loading' && setPortDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            backgroundColor: darkMode ? 'rgba(31, 41, 55, 0.95)' : 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(10px)',
            border: '1px solid',
            borderColor: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
            overflow: 'hidden'
          }
        }}
      >
        <Box sx={{ position: 'relative' }}>
          {/* Background gradient */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 120,
              background: 'linear-gradient(to bottom, rgba(79, 70, 229, 0.1), transparent)',
              opacity: darkMode ? 0.6 : 0.3,
              pointerEvents: 'none'
            }}
          />

          <DialogTitle sx={{
            pt: 3,
            pb: 1,
            textAlign: 'center',
            color: darkMode ? 'rgba(255, 255, 255, 0.9)' : '#1A1B25',
            fontWeight: 600
          }}>
            {portAction === 'confirm_sim' ? 'Confirm SIM & Initiate Port' : 'Initiate Number Port'}
          </DialogTitle>
          <DialogContent>
            {portingStatus === 'success' ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <Avatar
                    sx={{
                      width: 60,
                      height: 60,
                      bgcolor: 'rgba(16, 185, 129, 0.1)',
                      color: '#10B981',
                      margin: '0 auto 16px'
                    }}
                  >
                    <Check size={30} />
                  </Avatar>
                </motion.div>
                <Typography variant="h6" sx={{ mb: 1, color: darkMode ? 'rgba(255, 255, 255, 0.9)' : '#1A1B25' }}>
                  Port Initiated Successfully
                </Typography>
                <Typography sx={{ color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(55, 65, 81, 0.8)' }}>
                  Your number port is now in progress. This typically takes 1-2 business days to complete.
                </Typography>
              </Box>
            ) : (
              <>
                <Typography sx={{
                  mb: 3,
                  textAlign: 'center',
                  color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(55, 65, 81, 0.8)'
                }}>
                  {portAction === 'confirm_sim' ? (
                    'Confirm that you\'ve received your SIM card and want to initiate the number porting process'
                  ) : (
                    'Initiate the process to port your existing number to your new device'
                  )}
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <Typography sx={{
                    mb: 1.5,
                    fontWeight: 500,
                    color: darkMode ? 'rgba(255, 255, 255, 0.8)' : 'rgba(17, 24, 39, 0.8)'
                  }}>
                    Verify the last 4 digits of your temporary number
                  </Typography>
                  <TextField
                    fullWidth
                    label="Last 4 digits"
                    value={verificationCode}
                    onChange={(e) => {
                      // Only allow numbers and limit to 4 digits
                      const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 4);
                      setVerificationCode(value);
                    }}
                    disabled={portingStatus === 'loading'}
                    placeholder="1234"
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        '& fieldset': {
                          borderColor: darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                        },
                        '&:hover fieldset': {
                          borderColor: darkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#4F46E5',
                        },
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#4F46E5',
                      },
                    }}
                    InputProps={{
                      inputProps: {
                        pattern: '[0-9]*',
                        maxLength: 4,
                        style: { textAlign: 'center', letterSpacing: '0.5em', fontSize: '1.2rem' }
                      }
                    }}
                  />
                </Box>

                {portingError && (
                  <Alert
                    severity="error"
                    sx={{
                      mb: 2,
                      borderRadius: '12px'
                    }}
                  >
                    {portingError}
                  </Alert>
                )}

                <Typography variant="caption" sx={{
                  display: 'block',
                  textAlign: 'center',
                  color: darkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(107, 114, 128, 0.8)'
                }}>
                  This will initiate the process to port your existing phone number.
                  Once started, your service with your previous carrier will be terminated.
                </Typography>
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center' }}>
            {portingStatus === 'success' ? (
              <Button
                onClick={() => setPortDialogOpen(false)}
                variant="outlined"
                sx={{
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 4,
                  borderColor: darkMode ? 'rgba(99, 102, 241, 0.5)' : 'rgba(79, 70, 229, 0.5)',
                  color: darkMode ? '#818cf8' : '#4F46E5',
                  '&:hover': {
                    borderColor: darkMode ? 'rgba(99, 102, 241, 0.8)' : 'rgba(79, 70, 229, 0.8)',
                    backgroundColor: darkMode ? 'rgba(99, 102, 241, 0.1)' : 'rgba(79, 70, 229, 0.05)'
                  }
                }}
              >
                Close
              </Button>
            ) : (
              <>
                <Button
                  onClick={() => setPortDialogOpen(false)}
                  disabled={portingStatus === 'loading'}
                  sx={{
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 500,
                    color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(55, 65, 81, 0.8)'
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handlePortSubmit}
                  disabled={verificationCode.length !== 4 || portingStatus === 'loading'}
                  variant="contained"
                  sx={{
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 4,
                    background: 'linear-gradient(135deg, #4F46E5, #7C3AED)',
                    boxShadow: '0 4px 12px rgba(79, 70, 229, 0.2)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #4338CA, #6D28D9)',
                      boxShadow: '0 6px 18px rgba(79, 70, 229, 0.25)'
                    },
                    '&.Mui-disabled': {
                      background: darkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
                      color: darkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)'
                    }
                  }}
                  endIcon={portingStatus === 'loading' ? <CircularProgress size={16} color="inherit" /> : null}
                >
                  {portAction === 'confirm_sim' ? 'Confirm & Initiate Port' : 'Initiate Port'}
                </Button>
              </>
            )}
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
};

export default CircleTabPanelNavigation;